import { db } from '../firebase';
import { supabase } from '../supabase';
import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    query,
    orderBy,
} from 'firebase/firestore';

/**
 * Resilient Database Service
 *
 * Every read tries Firebase first and falls back to Supabase automatically.
 * Every write goes to BOTH databases so they stay in sync.
 * If one database is down, the site keeps working normally.
 */

// Track availability so we don't keep hammering a failed DB
let firebaseAvailable = true;
let supabaseAvailable = true;

// Re-check availability every 30 seconds after a failure
let firebaseRetryTimeout = null;
let supabaseRetryTimeout = null;

function markFirebaseDown() {
    if (firebaseAvailable) {
        firebaseAvailable = false;
        console.warn('⚠️ Firebase appears to be unavailable. Using Supabase as fallback.');
        clearTimeout(firebaseRetryTimeout);
        firebaseRetryTimeout = setTimeout(() => {
            firebaseAvailable = true;
            console.log('🔄 Retrying Firebase connection...');
        }, 30000);
    }
}

function markSupabaseDown() {
    if (supabaseAvailable) {
        supabaseAvailable = false;
        console.warn('⚠️ Supabase appears to be unavailable. Using Firebase as primary.');
        clearTimeout(supabaseRetryTimeout);
        supabaseRetryTimeout = setTimeout(() => {
            supabaseAvailable = true;
            console.log('🔄 Retrying Supabase connection...');
        }, 30000);
    }
}

/** Convert Firebase timestamps / Date objects to ISO strings */
function sanitizeForSupabase(data, firebaseId) {
    // Serialise timestamps in the JSONB snapshot
    const jsonSafe = {};
    for (const [key, val] of Object.entries(data)) {
        if (val === null || val === undefined) {
            jsonSafe[key] = null;
        } else if (val?.toDate) {
            jsonSafe[key] = val.toDate().toISOString();
        } else if (val instanceof Date) {
            jsonSafe[key] = val.toISOString();
        } else {
            jsonSafe[key] = val;
        }
    }

    // Only send firebase_id + full JSONB snapshot — never individual columns.
    // This means zero schema dependency and zero "column not found" errors.
    const out = { data: jsonSafe };
    if (firebaseId) out.firebase_id = firebaseId;
    return out;
}

/** Strip Supabase-specific columns and normalise the id field */
function normaliseSupabaseRow(row) {
    const { firebase_id, id, created_at, updated_at, ...rest } = row;
    return { id: firebase_id || String(id), ...rest };
}

// ─────────────────────────────────────────────────────────────────────────────
// READ
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch all documents from a Firestore collection / Supabase table.
 *
 * @param {string} collectionName
 * @param {{ orderByField?: string, orderDirection?: 'asc'|'desc' }} [options]
 * @returns {Promise<Array>}
 */
export async function getCollection(collectionName, options = {}) {
    // ── Try Firebase first ──────────────────────────────────────────────────────
    if (firebaseAvailable) {
        try {
            let q = collection(db, collectionName);
            if (options.orderByField) {
                q = query(q, orderBy(options.orderByField, options.orderDirection || 'desc'));
            }
            const snapshot = await getDocs(q);
            const results = [];
            snapshot.forEach((d) => results.push({ id: d.id, ...d.data() }));
            firebaseAvailable = true;
            return results;
        } catch (err) {
            console.error(`❌ Firebase read failed (${collectionName}):`, err.message);
            markFirebaseDown();
        }
    }

    // ── Fallback: Supabase ──────────────────────────────────────────────────────
    if (supabaseAvailable) {
        try {
            let q = supabase.from(collectionName).select('*');
            if (options.orderByField) {
                q = q.order(options.orderByField, {
                    ascending: options.orderDirection !== 'desc',
                });
            }
            const { data, error } = await q;
            if (error) throw error;
            supabaseAvailable = true;
            return (data || []).map(normaliseSupabaseRow);
        } catch (err) {
            console.error(`❌ Supabase read also failed (${collectionName}):`, err.message);
            markSupabaseDown();
        }
    }

    console.error(`❌ Both databases unavailable for ${collectionName}. Returning empty.`);
    return [];
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Add a document. Writes to Firebase first, then mirrors to Supabase.
 * Falls back to Supabase-only if Firebase is down.
 *
 * @param {string} collectionName
 * @param {object} data
 * @returns {Promise<string>} the new document id
 */
export async function addDocument(collectionName, data) {
    let firebaseId = null;
    let firebaseOk = false;

    // Write to Firebase
    if (firebaseAvailable) {
        try {
            const ref = await addDoc(collection(db, collectionName), data);
            firebaseId = ref.id;
            firebaseOk = true;
        } catch (err) {
            console.error(`❌ Firebase addDoc failed (${collectionName}):`, err.message);
            markFirebaseDown();
        }
    }

    // Write (mirror) to Supabase
    if (supabaseAvailable) {
        try {
            const supaData = sanitizeForSupabase(data, firebaseId);
            const { data: rows, error } = await supabase
                .from(collectionName)
                .insert([supaData])
                .select();
            if (error) throw error;
            supabaseAvailable = true;

            // If Firebase was down, return the Supabase row id instead
            if (!firebaseOk) {
                return String(rows[0]?.firebase_id || rows[0]?.id);
            }
        } catch (err) {
            console.error(`❌ Supabase insert failed (${collectionName}):`, err.message);
            markSupabaseDown();
        }
    }

    if (!firebaseOk && !supabaseAvailable) {
        throw new Error('Both databases are unavailable. Please try again later.');
    }

    return firebaseId;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Update an existing document in both databases.
 *
 * @param {string} collectionName
 * @param {string} docId  Firebase document id
 * @param {object} data   Partial update data
 */
export async function updateDocument(collectionName, docId, data) {
    let anySuccess = false;

    // Update Firebase
    if (firebaseAvailable) {
        try {
            await updateDoc(doc(db, collectionName, docId), data);
            firebaseAvailable = true;
            anySuccess = true;
        } catch (err) {
            console.error(`❌ Firebase updateDoc failed (${collectionName}/${docId}):`, err.message);
            markFirebaseDown();
        }
    }

    // Update Supabase
    if (supabaseAvailable) {
        try {
            const supaData = sanitizeForSupabase(data);
            const { error } = await supabase
                .from(collectionName)
                .update(supaData)
                .eq('firebase_id', docId);
            if (error) throw error;
            supabaseAvailable = true;
            anySuccess = true;
        } catch (err) {
            console.error(`❌ Supabase update failed (${collectionName}/${docId}):`, err.message);
            markSupabaseDown();
        }
    }

    if (!anySuccess) {
        throw new Error('Both databases are unavailable. Please try again later.');
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Delete a document from both databases.
 *
 * @param {string} collectionName
 * @param {string} docId  Firebase document id
 */
export async function deleteDocument(collectionName, docId) {
    let anySuccess = false;

    // Delete from Firebase
    if (firebaseAvailable) {
        try {
            await deleteDoc(doc(db, collectionName, docId));
            firebaseAvailable = true;
            anySuccess = true;
        } catch (err) {
            console.error(`❌ Firebase deleteDoc failed (${collectionName}/${docId}):`, err.message);
            markFirebaseDown();
        }
    }

    // Delete from Supabase
    if (supabaseAvailable) {
        try {
            const { error } = await supabase
                .from(collectionName)
                .delete()
                .eq('firebase_id', docId);
            if (error) throw error;
            supabaseAvailable = true;
            anySuccess = true;
        } catch (err) {
            console.error(`❌ Supabase delete failed (${collectionName}/${docId}):`, err.message);
            markSupabaseDown();
        }
    }

    if (!anySuccess) {
        throw new Error('Both databases are unavailable. Please try again later.');
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// STATUS HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/** Returns current health status of both databases */
export function getDatabaseStatus() {
    return { firebaseAvailable, supabaseAvailable };
}
